import posthog from "posthog-js";
import {
  analyticsReady,
  reportSlugFromPath,
} from "@/app/reports/_shared/analytics";
import { bookingContextEnabled } from "./booking-context";

/**
 * The one funnel event this site emits outside `/reports/*`, and the identify
 * call that makes the rest of the funnel joinable.
 *
 * It lives here rather than in `app/reports/_shared/analytics.ts` because that
 * file says what it is in its first line — "every event this DIRECTORY emits" —
 * and a lead form on `/contact` is not a report event. It still imports
 * `reportSlugFromPath` and `analyticsReady` from there rather than restating
 * them: that function is deliberately the single place deciding how
 * identifiable a prospect is in our analytics, and a second copy of the regex
 * here would mean coarsening it no longer coarsens everything.
 */
export const LEAD_CAPTURED = "lead_captured";

/** B1 keeps one opaque browser id through the report, CTA, lead and booking. */
export function preserveAnonymousLeadIdentity(enabled = bookingContextEnabled()): boolean {
  return enabled;
}

type LeadCapturedArgs = {
  /** Which form fired it — `"contact"`, `"lead-generation-hero"`. */
  source: string;
  /** Legacy mode identifies it; B1 preserves the browser's anonymous id. */
  email: string;
  /** `usePathname()` at the call site. */
  pathname: string | null;
  /** Anything else worth grouping by. No raw message or comment bodies. */
  properties?: Record<string, unknown>;
};

/**
 * Identify the person by email, then record the capture.
 *
 * **Order matters.** `identify` first, `capture` second, so the event lands on
 * the identified person rather than on the anonymous one and needing a merge
 * to find its way home.
 *
 * Identifying is the point of this function, more than the event is. Until
 * now every event on this site carried the anonymous per-browser `distinct_id`
 * PostHog generates on first load, so a prospect who read their report on a
 * phone and filled a form on a laptop was two unrelated people and no event
 * could ever be joined to a contact. Keying on the email they typed collapses
 * those, and — because PostHog merges the anonymous person into the identified
 * one — retroactively claims the `report_viewed` and `report_cta_clicked`
 * events that browser already sent.
 *
 * Email is lowercased and trimmed: `Ravi@Acme.com` and `ravi@acme.com` are one
 * buyer, and two `distinct_id`s that differ only in case would defeat the whole
 * exercise.
 *
 * Call it only AFTER a submission has actually succeeded. An event fired on
 * click, or from a form that discards its input, is a `lead_captured` with no
 * lead behind it anywhere — worse than the silence it replaced.
 *
 * Like everything else here it is gated on `posthog.__loaded` and wrapped in
 * `try`/`catch`: a local `next build` with no `.env` initialises PostHog with
 * `undefined`, and analytics must never be the reason a form submission
 * appears to fail to the person who filled it in.
 */
export function captureLeadCaptured({
  source,
  email,
  pathname,
  properties,
}: LeadCapturedArgs): void {
  try {
    if (!analyticsReady(posthog)) return;

    const normalisedEmail = email.trim().toLowerCase();
    if (!normalisedEmail) return;

    // A pre-existing email-shaped PostHog cookie is excluded by
    // `safeAnonymousDistinctId`; do not reset or merge it automatically.
    if (!preserveAnonymousLeadIdentity()) {
      posthog.identify(normalisedEmail, { email: normalisedEmail });
    }

    // Null on both of today's call sites — neither form lives under
    // `/reports/*` — so it is omitted rather than sent as a permanently empty
    // column. It is read anyway so that a form placed on a report page later
    // carries the slug without anyone remembering to wire it up. The report →
    // lead join does not depend on it: `identify` above is what connects this
    // submission to the `report_viewed` this browser already sent.
    const reportSlug = reportSlugFromPath(pathname);

    posthog.capture(LEAD_CAPTURED, {
      source,
      ...(reportSlug ? { report_slug: reportSlug } : {}),
      ...properties,
    });
  } catch {
    // Never let analytics break a submission the visitor already completed.
  }
}
